import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, TextField, Typography, IconButton,
    MenuItem, Select, FormControl, InputLabel, Stack, Divider,
    Chip, Alert, CircularProgress, Grid, Paper, useTheme, Dialog,
    DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
    Add as AddIcon,
    Close as RemoveIcon,
    Save as SaveIcon,
    Lock as LockIcon,
    FlashOn as PresentIcon,
    AutoGraph as NextIcon,
    ReportProblem as IssueIcon,
    Assignment as AobIcon,
    ArrowForwardIos as DividerIcon,
    DeleteOutline as DeleteIcon,
} from '@mui/icons-material';

// ── CONFIGURAÇÕES ───────────────────────────────────────────────────────────
const TECHNOLOGIES = ['2G', '4G', '5G'];
const VENDORS = ['Nokia', 'Ericsson', 'Other'];

const emptyActivity = () => ({ technology: '', vendor: '', description: '' });
const emptyTextItem = () => ({ description: '' });

// ── COMPONENTE DE ITEM DE ATIVIDADE ─────────────────────────────────────────
const ActivityCard = ({ item, index, onUpdate, onRemove, color, isEditable }) => (
    <Box sx={{ 
        position: 'relative', mb: 2, p: 2, 
        borderRadius: 2, bgcolor: 'background.paper',
        border: '1px solid', borderColor: 'divider',
        transition: '0.2s',
        '&:hover': { borderColor: `${color}.main`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }
    }}>
        <Stack spacing={2}>
            <Stack direction="row" spacing={1}>
                <FormControl fullWidth size="small">
                    <InputLabel>Tech</InputLabel>
                    <Select
                        value={item.technology || ''}
                        label="Tech"
                        onChange={(e) => onUpdate(index, 'technology', e.target.value)}
                        disabled={!isEditable}
                    >
                        {TECHNOLOGIES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                    <InputLabel>Vendor</InputLabel>
                    <Select
                        value={item.vendor || ''}
                        label="Vendor"
                        onChange={(e) => onUpdate(index, 'vendor', e.target.value)}
                        disabled={!isEditable}
                    >
                        {VENDORS.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </Select>
                </FormControl>
            </Stack>
            <TextField
                fullWidth multiline minRows={3} maxRows={8}
                placeholder="Describe activity..."
                value={item.description || ''}
                onChange={(e) => onUpdate(index, 'description', e.target.value)}
                disabled={!isEditable}
                size="small"
            />
        </Stack>
        {isEditable && (
            <IconButton 
                size="small" onClick={() => onRemove(index)}
                sx={{ 
                    position: 'absolute', top: 6, right: 6, 
                    bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                    width: 22, height: 22,
                    '&:hover': { color: 'error.main', bgcolor: 'error.lighter' } 
                }}
            >
                <RemoveIcon sx={{ fontSize: 14 }} />
            </IconButton>
        )}
    </Box>
);

// ── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const WeeklyUpdateForm = ({ submission, weekNumber, year, isEditable = true, saving, onSave, onDelete, copiedData, onCopiedDataConsumed }) => {
    const theme = useTheme();
    const [presentWeek, setPresentWeek] = useState([emptyActivity()]);
    const [nextWeek, setNextWeek] = useState([emptyActivity()]);
    const [issues, setIssues] = useState([emptyTextItem()]);
    const [aob, setAob] = useState([emptyTextItem()]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    useEffect(() => {
        if (submission) {
            setPresentWeek(submission.presentWeekActivities?.length > 0 ? submission.presentWeekActivities : [emptyActivity()]);
            setNextWeek(submission.nextWeekActivities?.length > 0 ? submission.nextWeekActivities : [emptyActivity()]);
            setIssues(submission.issues?.length > 0 ? submission.issues : [emptyTextItem()]);
            setAob(submission.aob?.length > 0 ? submission.aob : [emptyTextItem()]);
        } else {
            setPresentWeek([emptyActivity()]);
            setNextWeek([emptyActivity()]);
            setIssues([emptyTextItem()]);
            setAob([emptyTextItem()]);
        }
    }, [submission]);

    // ── Apply copied data from history ───────────────────────────────────────
    useEffect(() => {
        if (copiedData) {
            setPresentWeek(copiedData.presentWeekActivities?.length > 0 ? copiedData.presentWeekActivities : [emptyActivity()]);
            setNextWeek(copiedData.nextWeekActivities?.length > 0 ? copiedData.nextWeekActivities : [emptyActivity()]);
            setIssues(copiedData.issues?.length > 0 ? copiedData.issues : [emptyTextItem()]);
            setAob(copiedData.aob?.length > 0 ? copiedData.aob : [emptyTextItem()]);

            if (onCopiedDataConsumed) onCopiedDataConsumed();
        }
    }, [copiedData]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUpdate = useCallback((setter) => (index, field, value) => {
        setter((prev) => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
    }, []);

    const handleAdd = (setter, template) => () => setter((prev) => [...prev, template()]);
    const handleRemove = (setter, template) => (index) => setter((prev) => 
        prev.length > 1 ? prev.filter((_, i) => i !== index) : [template()]
    );

    const onFormSubmit = () => {
        onSave({
            weekNumber, year,
            presentWeekActivities: presentWeek.filter(a => a.description),
            nextWeekActivities: nextWeek.filter(a => a.description),
            issues: issues.filter(i => i.description.trim()),
            aob: aob.filter(a => a.description.trim()),
        });
    };

    const handleConfirmDelete = () => {
        setDeleteDialogOpen(false);
        if (onDelete) onDelete();
    };

    const hasSubmission = !!submission;

    return (
        <Box sx={{ py: 4, px: { xs: 1, md: 2 } }}>
            
            {/* CABEÇALHO */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: -1 }}>
                        Weekly Update
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Reporting for <span style={{ color: theme.palette.primary.main }}>Week {weekNumber}</span> • {year}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    {!isEditable && (
                        <Chip icon={<LockIcon />} label="READ ONLY" color="error" variant="filled" sx={{ borderRadius: 1.5, fontWeight: 700 }} />
                    )}
                    {isEditable && hasSubmission && onDelete && (
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDeleteDialogOpen(true)}
                            sx={{ textTransform: 'none', borderRadius: 2, fontSize: 12 }}
                        >
                            Delete Report
                        </Button>
                    )}
                </Stack>
            </Stack>

            <Paper elevation={0} sx={{ 
                borderRadius: 4, border: '1px solid', borderColor: 'divider',
                overflow: 'hidden', bgcolor: '#ffffff'
            }}>
                <Grid container>
                    
                    {/* COLUNA: PRESENT WEEK */}
                    <Grid item xs={12} md sx={{ p: 3 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                            <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.lighter', color: 'primary.main', display: 'flex' }}>
                                <PresentIcon fontSize="small" />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={800}>Present Week</Typography>
                        </Stack>
                        
                        {presentWeek.map((item, idx) => (
                            <ActivityCard 
                                key={`p-${idx}`} item={item} index={idx} isEditable={isEditable} color="primary"
                                onUpdate={handleUpdate(setPresentWeek)} onRemove={handleRemove(setPresentWeek, emptyActivity)} 
                            />
                        ))}
                        
                        {isEditable && (
                            <Button 
                                fullWidth startIcon={<AddIcon />} 
                                onClick={handleAdd(setPresentWeek, emptyActivity)}
                                sx={{ border: '1px dashed', borderColor: 'divider', py: 1.5, borderRadius: 2, textTransform: 'none' }}
                            >
                                Add Activity
                            </Button>
                        )}
                    </Grid>

                    {/* DIVISOR VISUAL COM ÍCONE */}
                    <Grid item sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center', width: '1px', position: 'relative' }}>
                        <Divider orientation="vertical" flexItem />
                    </Grid>

                    {/* COLUNA: NEXT WEEK */}
                    <Grid item xs={12} md sx={{ p: 3 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                            <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'secondary.lighter', color: 'secondary.main', display: 'flex' }}>
                                <NextIcon fontSize="small" />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={800}>Next Week</Typography>
                        </Stack>
                        
                        {nextWeek.map((item, idx) => (
                            <ActivityCard 
                                key={`n-${idx}`} item={item} index={idx} isEditable={isEditable} color="secondary"
                                onUpdate={handleUpdate(setNextWeek)} onRemove={handleRemove(setNextWeek, emptyActivity)} 
                            />
                        ))}
                        
                        {isEditable && (
                            <Button 
                                fullWidth color="secondary" startIcon={<AddIcon />} 
                                onClick={handleAdd(setNextWeek, emptyActivity)}
                                sx={{ border: '1px dashed', borderColor: 'divider', py: 1.5, borderRadius: 2, textTransform: 'none' }}
                            >
                                Add Activity
                            </Button>
                        )}
                    </Grid>

                    {/* COLUNA: STATUS SIDEBAR (ISSUES & AOB) */}
                    <Grid item xs={12} md={3} sx={{ bgcolor: '#fbfbfb', borderLeft: { md: '1px solid' }, borderColor: 'divider', p: 3 }}>
                        <Stack spacing={4}>
                            {/* ISSUES */}
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                    <IssueIcon color="error" fontSize="small" />
                                    <Typography variant="overline" fontWeight={800} color="error.main">Issues & Risks</Typography>
                                </Stack>
                                <Stack spacing={1.5}>
                                    {issues.map((item, idx) => (
                                        <Box key={`i-${idx}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                            <TextField 
                                                fullWidth size="small" variant="outlined" placeholder="Enter blocker..."
                                                multiline minRows={2} maxRows={5}
                                                value={item.description || ''} disabled={!isEditable}
                                                onChange={(e) => handleUpdate(setIssues)(idx, 'description', e.target.value)}
                                            />
                                            {isEditable && (
                                                <IconButton size="small" onClick={() => handleRemove(setIssues, emptyTextItem)(idx)} sx={{ mt: 0.5 }}>
                                                    <RemoveIcon fontSize="inherit"/>
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                    {isEditable && (
                                        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd(setIssues, emptyTextItem)} sx={{ mt: 1, fontSize: '0.7rem' }}>
                                            Add Risk
                                        </Button>
                                    )}
                                </Stack>
                            </Box>

                            <Divider />

                            {/* AOB */}
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                    <AobIcon color="action" fontSize="small" />
                                    <Typography variant="overline" fontWeight={800} color="text.secondary">Other Business</Typography>
                                </Stack>
                                <Stack spacing={1.5}>
                                    {aob.map((item, idx) => (
                                        <Box key={`a-${idx}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                            <TextField 
                                                fullWidth size="small" variant="outlined" placeholder="Additional notes..."
                                                multiline minRows={2} maxRows={5}
                                                value={item.description || ''} disabled={!isEditable}
                                                onChange={(e) => handleUpdate(setAob)(idx, 'description', e.target.value)}
                                            />
                                            {isEditable && (
                                                <IconButton size="small" onClick={() => handleRemove(setAob, emptyTextItem)(idx)} sx={{ mt: 0.5 }}>
                                                    <RemoveIcon fontSize="inherit"/>
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                    {isEditable && (
                                        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd(setAob, emptyTextItem)} sx={{ mt: 1, fontSize: '0.7rem' }}>
                                            Add Note
                                        </Button>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

                {/* BOTÃO DE ACÇÃO NO RODAPÉ */}
                <Box sx={{ 
                    p: 2.5, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
                    display: 'flex', justifyContent: 'center', position: 'sticky', bottom: 0, zIndex: 10
                }}>
                    <Button
                        variant="contained"
                        disabled={!isEditable || saving}
                        onClick={onFormSubmit}
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        sx={{ 
                            px: 12, py: 1.5, borderRadius: 3, textTransform: 'none', 
                            fontSize: '1rem', fontWeight: 800,
                            boxShadow: '0 8px 24px rgba(25, 118, 210, 0.25)'
                        }}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Paper>

            {/* ── DELETE CONFIRMATION DIALOG ──────────────────────────────────── */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Report</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete your report for Week {weekNumber}, {year}? 
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WeeklyUpdateForm;